// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import logger from "@insite/client-framework/Logger";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FloatingTooltipRef, FloatingTooltipStyles } from "@steiner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@steiner/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip, {
    DEFAULT_STEINER_OFFSET,
    DEFAULT_STEINER_STICKY_OFFSET,
} from "@steiner/App/Components/Menu/FloatingMenuTooltip";
import MiniMyAccount from "@steiner/App/Components/MiniMyAccount/MiniMyAccount";
import MiniSignIn from "@steiner/App/Components/MiniSignIn/MiniSignIn";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import { getMyAccountMapLinks } from "@steiner/XDL/Utilities/links";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    /**
     * XNG-Changes
     */
    accountManagementLinks = "accountManagementLinks",
    orderManagementLinks = "orderManagementLinks",
    isCompactHeader = "isCompactHeader",
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const { billToId, shipToId } = getSession(state);
    const billToState = getBillToState(state, getSession(state).billToId);
    const shipToState = getShipToState(state, getSession(state).shipToId);

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        /**
         * XNG-Changes
         */
        isAuthenticated: xIsAuthenticated(state),
        isMyAccountPage: getCurrentPage(state).type === "MyAccountPage",
        accountManagementLinks: getMyAccountMapLinks(state, ownProps.fields.accountManagementLinks),
        orderManagementLinks: getMyAccountMapLinks(state, ownProps.fields.orderManagementLinks),
        shouldLoadBillTo: !billToState.value && !billToState.isLoading,
        shouldLoadShipTo: !shipToState.value && !shipToState.isLoading,
        billToId,
        shipToId,
        isTabletScreen: state.xCommon.isTabletScreen,
        isSticky: state.xHeader.isSticky,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    /**
     * XNG-Changes
     */
    loadShipTo,
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    /**
     * XNG-Changes
     */
    myAccountHeaderClickable?: HeaderClickableStyles;
    signInHeaderClickable?: HeaderClickableStyles;
    floatingTooltipSignIn?: FloatingTooltipStyles;
    floatingTooltipMyAccount?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        /**
         * XNG-Changes
         */
        [fields.accountManagementLinks]: LinkModel[];
        [fields.orderManagementLinks]: LinkModel[];
        [fields.isCompactHeader]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

/**
 * XNG-Changes
 * Centralized method for generating the sign-in redirect URL.
 * Helps simplify upgrades by updating logic in one place,
 * which is then used across multiple components.
 *
 * @param {Object} props - The input parameters.
 * @param {string} [props.signInUrl] - The base URL for the sign-in page.
 * @param {string} props.pathname - The current path from the location object.
 * @param {string} props.search - The current query string from the location object.
 * @returns {string} The computed redirect URL.
 */
export const getSignInRedirectUrl = ({
    signInUrl,
    pathname,
    search,
}: {
    signInUrl?: string;
    pathname: string;
    search: string;
}): string => {
    if (!signInUrl) {
        logger.warn("No URL was found for SignInPage, defaulting to /SignIn");
        return "/MyAccount/SignIn";
    }

    const returnUrl = pathname + search;
    if (returnUrl === "/") {
        return signInUrl;
    }

    return `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`;
};

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    isAuthenticated,
    isMyAccountPage,
    accountManagementLinks,
    orderManagementLinks,
    shouldLoadBillTo,
    shouldLoadShipTo,
    billToId,
    shipToId,
    loadBillTo,
    loadShipTo,
    isTabletScreen,
    isSticky,
}: Props) => {
    /**
     * SNTRS-582
     */
    const [key, setKey] = useState(0);
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const tooltipRef = useRef<FloatingTooltipRef>(null);

    /**
     * XNG-Changes
     * Load BillTo and ShipTo
     */
    useEffect(() => {
        if (shouldLoadShipTo && billToId && shipToId) {
            loadShipTo({
                billToId,
                shipToId,
            });
        }

        if (shouldLoadBillTo && billToId) {
            loadBillTo({ billToId });
        }
    }, []);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            /**
             * XNG-Changes
             * Move the code to getSignInRedirectUrl method
             */
            history.push(
                getSignInRedirectUrl({ signInUrl, pathname: currentLocation.pathname, search: currentLocation.search }),
            );
            closePopup();
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        closePopup();
    };

    const closePopup = () => {
        /**
         * SNTRS-582
         */
        setKey(prevState => prevState + 1);

        tooltipRef.current?.close();
    };

    const onMyAccountHandler = () => {
        if (isMyAccountPage) {
            return;
        }

        history.push(myAccountPageLink.url);
        closePopup();
    };

    if (!isAuthenticated) {
        return (
            <FloatingMenuTooltip
                key={key}
                offset={isSticky ? DEFAULT_STEINER_STICKY_OFFSET : DEFAULT_STEINER_OFFSET}
                open={tooltipRef.current?.isOpen}
                ref={tooltipRef}
                triggerComponent={
                    <HeaderClickable
                        isOpen={tooltipRef.current?.isOpen}
                        onClick={onSignInHandler}
                        data-test-selector="signIn"
                        aria-label={translate("Sign In")}
                        isCompactHeader={fields.isCompactHeader}
                        extendedStyles={styles.signInHeaderClickable}
                    >
                        {translate("Sign In")}
                    </HeaderClickable>
                }
                enabled={!isTabletScreen}
                extendedStyles={styles.floatingTooltipSignIn}
            >
                <MiniSignIn closePopup={closePopup} />
            </FloatingMenuTooltip>
        );
    }

    return (
        <FloatingMenuTooltip
            key={key}
            offset={isSticky ? DEFAULT_STEINER_STICKY_OFFSET : DEFAULT_STEINER_OFFSET}
            open={tooltipRef.current?.isOpen}
            ref={tooltipRef}
            triggerComponent={
                <HeaderClickable
                    isOpen={tooltipRef.current?.isOpen}
                    onClick={onMyAccountHandler}
                    data-test-selector="myAccount"
                    aria-label={translate("My Account")}
                    isCompactHeader={fields.isCompactHeader}
                    extendedStyles={styles.myAccountHeaderClickable}
                >
                    {translate("My Account")}
                </HeaderClickable>
            }
            enabled={!isTabletScreen}
            extendedStyles={styles.floatingTooltipMyAccount}
        >
            <MiniMyAccount
                displayChangeCustomerLink={displayChangeCustomerLink}
                accountManagementLinks={accountManagementLinks}
                orderManagementLinks={orderManagementLinks}
                onSignOutHandler={onSignOutHandler}
            />
        </FloatingMenuTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
                isVisible: _ => false,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
                isVisible: _ => false,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
                isVisible: _ => false,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                // isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
                isVisible: _ => false,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                // isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
                isVisible: _ => false,
            },
            {
                displayName: "Account Management",
                name: fields.accountManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Url") {
                        const overriddenTitle = item.fields.overriddenTitle;
                        return overriddenTitle || value;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                displayName: "Product & Order Management",
                name: fields.orderManagementLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Url") {
                        const overriddenTitle = item.fields.overriddenTitle;
                        return overriddenTitle || value;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.isCompactHeader,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
